import {
  TaskStatus,
  type User,
  type TaskTemplate,
  type TaskTemplateFilter,
  Task,
  ObjectType,
  ResourceName,
  DateYMDString,
} from "@/types";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { composeDocumentLabel } from "@/utils/formatters/documentLabel";
import {
  parseISO,
  isToday,
  isYesterday,
  isTomorrow,
  isPast,
  format,
  isValid,
  startOfWeek,
  addWeeks,
  endOfWeek,
  isWithinInterval,
} from "date-fns";
import dayjs from "dayjs";
import { RaRecord } from "react-admin";

// ============================================================================
// CONSTANTS
// ============================================================================

export const DUE_OPTIONS = [
  { label: "Today", days: 0 },
  { label: "Tomorrow", days: 1 },
  { label: "In 3 days", days: 3 },
  { label: "Next week", days: 7 },
] as const;

export const DUE_OPTIONS_WITH_NONE = [...DUE_OPTIONS, { label: "No due date", days: null }] as const;

// Task status options are derived from the single canonical enum in `@/types`
// (`TASK_STATUS_CHOICES`). Do not reintroduce a second status list here — the
// duplicated/UPPERCASE picker entries in #5165 came from divergent sources
// (#6310 AC3).

export const RECORD_TYPES = [
  { id: ObjectType.STOPLOSS_CLAIMS, name: "Claim", resource: ResourceName.STOPLOSS_CLAIMS },
  { id: ObjectType.CLAIMANTS, name: "Claimant", resource: ResourceName.CLAIMANTS },
  { id: ObjectType.POLICIES, name: "Policy", resource: ResourceName.POLICIES },
  { id: ObjectType.CLIENTS, name: "Client", resource: ResourceName.CLIENTS },
  { id: ObjectType.REIMBURSEMENTS, name: "Reimbursement", resource: ResourceName.REIMBURSEMENTS },
  { id: ObjectType.DOCUMENTS, name: "Document", resource: ResourceName.DOCUMENTS },
] as const;

// ============================================================================
// ENUMS
// ============================================================================

export enum TaskTab {
  MY_TASKS = "myTasks",
  DUE_NEXT_WEEK = "dueNextWeek",
  ALL_TASKS = "allTasks",
  OVERDUE = "overdue",
  UNASSIGNED = "unassigned",
}

// ============================================================================
// USER HELPERS
// ============================================================================

/**
 * Get display name for a user, with optional "(me)" suffix for current user
 */
export const getAssigneeName = (user: User | null | undefined, currentUserId?: string): string => {
  if (!user) return "Unknown";
  const name = user.full_name || user.email || "Unknown";
  return currentUserId && user.id === currentUserId ? `${name} (me)` : name;
};

/**
 * Get display information for a user (name, job title, initials)
 */
export const getUserDisplayInfo = (user: User | null) => {
  if (!user) {
    return {
      displayName: "Unknown User",
      jobTitle: undefined,
      initials: "?",
    };
  }

  const displayName = user.full_name || user.preferred_name || user.email || user.user_principal_name || "Unknown User";
  const jobTitle = user.job_title ?? undefined;
  const initials = getInitials(displayName);

  return { displayName, jobTitle, initials };
};

/**
 * Get initials from a name string (up to 2 characters)
 */
export const getInitials = (name?: string): string => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

// ============================================================================
// RECORD HELPERS
// ============================================================================

/**
 * Get display name for a record (claimant, claim, policy, etc.)
 */
export const getRecordDisplayName = (record: RaRecord): string => {
  if (!record) return "";
  return (
    record.claimant_name ||
    record.client_legal_name ||
    record.title ||
    // Documents expose their human-readable label as `name`; without this they
    // fell through to the UUID, which also broke server-side `q` search matching.
    record.name ||
    record.aegle_id ||
    record.label ||
    record.id ||
    ""
  );
};

/**
 * A record's human-identifiable label, split into a primary line and an
 * optional secondary line. Mirrors each resource's Show-page header
 * (title + subtitle) so the record picker identifies records the same way
 * the rest of the app does — see ADO #5250. aegle_id is a last-resort
 * fallback only; it is not how users recognize records.
 */
export interface RecordIdentity {
  primary: string;
  secondary?: string;
}

/** Join non-empty parts with the app's standard middot separator. */
const joinIdentityParts = (...parts: (string | null | undefined)[]): string =>
  parts.filter((p): p is string => Boolean(p && p.trim())).join(" · ");

/** Policy year chip text, mirroring PolicyShow's policyYearTag (en dash for ranges). */
const policyYearLabel = (record: RaRecord): string | undefined => {
  const start = (record.effective_start_date as string | undefined)?.slice(0, 4);
  const end = (record.effective_end_date as string | undefined)?.slice(0, 4);
  if (start && end) return start === end ? start : `${start}–${end}`;
  return start || end || undefined;
};

/**
 * Compose a record's picker label to match its Show-page header. Each branch
 * intentionally mirrors the corresponding *Show.tsx PageHeader title/subtitle
 * so the picker and the show page stay in lockstep. Falls back to
 * getRecordDisplayName for unmapped types.
 */
export const getRecordIdentity = (objectType: ObjectType | null, record: RaRecord): RecordIdentity => {
  if (!record) return { primary: "" };

  switch (objectType) {
    case ObjectType.POLICIES: {
      // PolicyShow: title = carrier company name (vendor_company_name, enriched
      // from vendor_id_ref); subtitle = policyholder (client_legal_name is not in
      // the policy list payload); year chip from the effective dates. Never lead
      // with the generic word "Policy" (per UX feedback).
      const carrier = record.vendor_company_name as string | undefined;
      const holder =
        (record.source_data?.policyholder as string | undefined) || (record.client_legal_name as string | undefined);
      const year = policyYearLabel(record);
      if (carrier) return { primary: carrier, secondary: joinIdentityParts(holder, year) || undefined };
      return { primary: holder || (record.aegle_id as string) || "Policy", secondary: year };
    }
    case ObjectType.CLAIMANTS:
      // Claimant name only — the client line is noise in the picker (per UX feedback).
      return { primary: (record.claimant_name as string) || (record.aegle_id as string) || "Claimant" };
    case ObjectType.STOPLOSS_CLAIMS: {
      // "{Type} Claim · {paid amount}" with a "claimant · client" subtitle.
      const type = record.stoploss_claim_type ? formatToTitleCase(record.stoploss_claim_type as string) : "";
      const base = type ? `${type} Claim` : "Stop-Loss Claim";
      const paid = Number(record.total_paid_amount);
      const hasPaid =
        record.total_paid_amount !== null && record.total_paid_amount !== undefined && Number.isFinite(paid);
      return {
        primary: hasPaid ? `${base} · ${formatUSD(paid)}` : base,
        secondary:
          joinIdentityParts(record.claimant_name as string, record.client_legal_name as string) ||
          (record.aegle_id as string) ||
          undefined,
      };
    }
    case ObjectType.REIMBURSEMENTS: {
      // ReimbursementShow: title = "Reimbursement · {amount}"; subtitle = claimant · client.
      const raw = record.requested_amount;
      const amount = Number(raw);
      const hasAmount = raw !== null && raw !== undefined && raw !== "" && Number.isFinite(amount);
      return {
        primary: hasAmount ? `Reimbursement · ${formatUSD(amount)}` : "Reimbursement",
        secondary:
          joinIdentityParts(record.claimant_name as string, record.client_legal_name as string) ||
          (record.aegle_id as string) ||
          undefined,
      };
    }
    case ObjectType.DOCUMENTS:
      // DocumentShow: title = name (with sheet); subtitle = sheet name.
      return {
        primary: composeDocumentLabel({
          name: record.name as string | undefined,
          object_metadata: record.object_metadata as { sheet_name?: string } | undefined,
        }),
      };
    case ObjectType.CLIENTS:
    default:
      // ClientShow: title = client_legal_name. Default chain covers the rest.
      return { primary: getRecordDisplayName(record) };
  }
};

/**
 * Get the record type configuration by ObjectType
 */
export const getRecordTypeConfig = (objectType: ObjectType | null) => {
  return RECORD_TYPES.find((t) => t.id === objectType) ?? null;
};

// ObjectType spells two values differently from template applicable_object_types
// ("stoploss-claims" vs "stoploss_claims", singular "reimbursement").
const normalizeObjectType = (type: string): string => {
  const t = type.toLowerCase().replace(/-/g, "_");
  return t === "reimbursement" ? "reimbursements" : t;
};

// The admin template form sets record_subject but never applicable_object_types,
// so admin-created templates carry only the subject. "claim" is the subject
// toRecordSubject sends for stop-loss claims.
const RECORD_SUBJECT_TO_OBJECT_TYPE: Record<string, string> = {
  claimant: "claimants",
  client: "clients",
  policy: "policies",
  stoploss_claim: "stoploss_claims",
  claim: "stoploss_claims",
  reimbursement: "reimbursements",
  endorsement: "endorsements",
};

/**
 * Whether a template may be applied to a record of the given type. GET
 * /task-templates ignores `applicable_object_types` (#7655), so callers filter
 * client-side. Without listed types the template's record_subject decides; a
 * template with neither applies everywhere.
 */
export const templateAppliesTo = (template: TaskTemplate, objectType: ObjectType | null | undefined): boolean => {
  const raw = template.applicable_object_types;
  const listed = raw === undefined ? [] : Array.isArray(raw) ? raw : [raw];
  const fromSubject = template.record_subject && RECORD_SUBJECT_TO_OBJECT_TYPE[template.record_subject.toLowerCase()];
  const types = listed.length > 0 ? listed : fromSubject ? [fromSubject] : [];
  if (types.length === 0) return true;
  if (!objectType) return false;
  const target = normalizeObjectType(objectType);
  return types.some((t) => normalizeObjectType(t) === target);
};

/**
 * Map object type to record_subject for backend filtering
 */
export const toRecordSubject = (objectType?: string): string | undefined => {
  if (!objectType) return undefined;
  const mapping: Record<string, string> = {
    "stoploss-claims": "claim",
    claimants: "claimant",
    clients: "client",
    policies: "policy",
    endorsements: "endorsement",
    reimbursement: "reimbursement",
    documents: "policy",
  };
  return mapping[objectType];
};

// ============================================================================
// DATE HELPERS
// ============================================================================

/**
 * Format due date as a short label (Today, Tomorrow, MMM D)
 * Used in chips and compact displays
 */
export const formatDueLabel = (date?: string | null): string | null => {
  if (!date) return null;

  const d = dayjs(date);
  const today = dayjs();
  const tomorrow = today.add(1, "day");

  if (d.isSame(today, "day")) return "Today";
  if (d.isSame(tomorrow, "day")) return "Tomorrow";
  if (d.isBefore(today, "day")) return `Overdue (${d.format("MMM D")})`;
  return d.format("MMM D");
};

/**
 * Format due date as a badge with appropriate styling tone
 * Used in list displays where color coding is needed
 */
export const formatDueBadge = (due?: string | null): { label: string; tone: "warning" | "error" | "default" } => {
  if (!due) return { label: "", tone: "default" };

  const d = parseISO(due);
  if (!isValid(d)) return { label: "", tone: "default" };

  if (isToday(d)) return { label: "Today", tone: "warning" };
  if (isTomorrow(d)) return { label: "Tomorrow", tone: "default" };
  if (isYesterday(d)) return { label: "Yesterday", tone: "error" };
  if (isPast(d)) return { label: format(d, "MMM d"), tone: "error" };

  return { label: format(d, "MMM d"), tone: "default" };
};

/**
 * Check if a due date is overdue (before today and not completed)
 */
export const isOverdue = (dueDate?: string | null, completed?: boolean): boolean => {
  if (!dueDate || completed) return false;
  return dayjs(dueDate).isBefore(dayjs(), "day");
};

/**
 * Format date as UTC YYYY-MM-DD
 */
export const toYMD = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * Add days to current date in UTC and return YYYY-MM-DD string
 */
export const addDaysUTC = (days: number): string => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return toYMD(date);
};

/**
 * Add days using dayjs and return YYYY-MM-DD string
 */
export const addDays = (days: number): DateYMDString => {
  return dayjs().add(days, "day").format("YYYY-MM-DD") as DateYMDString;
};

// ============================================================================
// STATUS HELPERS
// ============================================================================

/**
 * Format task status for display
 */
export const formatStatus = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.IN_PROGRESS:
      return formatToTitleCase(TaskStatus.IN_PROGRESS);
    case TaskStatus.COMPLETED:
      return formatToTitleCase(TaskStatus.COMPLETED);
    case TaskStatus.OPEN:
      return formatToTitleCase(TaskStatus.OPEN);
    case TaskStatus.CANCELLED:
      return formatToTitleCase(TaskStatus.CANCELLED);
    default:
      return status;
  }
};

// ============================================================================
// FILTER HELPERS
// ============================================================================

/**
 * Build filter for task templates based on context
 */
export const buildTemplateFilter = (objectType?: string, searchQuery?: string): TaskTemplateFilter => {
  const filter: TaskTemplateFilter = { is_active: true };

  if (objectType) {
    const recordSubject = toRecordSubject(objectType);
    if (recordSubject) {
      filter.record_subject = recordSubject;
    }
    filter.applicable_object_types = objectType;
  }

  if (searchQuery?.trim()) {
    const trimmed = searchQuery.trim();
    filter.trigger_event = trimmed;
    filter.title = trimmed;
  }

  return filter;
};

/**
 * Filter and sort tasks by selected tab
 */
export const getTasksBySelectedTab = (
  tasks: Task[],
  selectedTab: TaskTab,
  validUserIds: string[],
  searchQuery?: string
) => {
  const today = new Date().toISOString().split("T")[0];

  if (selectedTab === TaskTab.ALL_TASKS && !searchQuery) return tasks;

  return tasks.filter((task: Task) => {
    // Search filter
    if (searchQuery) {
      const matchesSearch =
        task.title?.toLowerCase().includes(searchQuery) || task.description?.toLowerCase().includes(searchQuery);
      if (!matchesSearch) return false;
    }

    if (selectedTab === TaskTab.DUE_NEXT_WEEK) {
      if (task.status === TaskStatus.COMPLETED) return false;
      const dueDate = task.due_date;
      if (!dueDate) return false;
      const parsedDate = parseISO(dueDate);
      if (!isValid(parsedDate)) return false;
      const nextWeekStart = startOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 });
      const nextWeekEnd = endOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 });
      return isWithinInterval(parsedDate, { start: nextWeekStart, end: nextWeekEnd });
    }

    if (selectedTab === TaskTab.OVERDUE) {
      const dueISO = task.due_date ?? "";
      const dueDateOnly = dueISO.split("T")[0];
      if (task.status === TaskStatus.COMPLETED) return false;
      if (!dueDateOnly || dueDateOnly >= today) return false;
      return true;
    }

    if (selectedTab === TaskTab.UNASSIGNED) {
      if (!task.assigned_user_id_ref) return true;
      return !validUserIds.includes(task.assigned_user_id_ref);
    }

    return true;
  });
};
